import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Checkbox,
  Button,
  AppBar,
  Toolbar,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  TextField,
  LinearProgress,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link, useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import axiosInstance from "../api/axiosConfig";
import ViewModeSwitch from "./ViewModeSwitch";
import PlaylistDialog from "./PlaylistDialog";
import ConfirmDialog from "./ConfirmDialog";

interface Video {
  vid: string;
  title: string;
  thumbnailFilename: String;
  youtubeId: String;
  fileName: string;
  filesize: number;
  duration: number;
  resolution: string | null;
  hasHls?: boolean;
  previewFileName?: string;
}

interface VideoGridProps {
  refreshTrigger: number;
  searchResults?: Video[];
}

const VideoGrid: React.FC<VideoGridProps> = ({ refreshTrigger, searchResults }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVid, setSelectedVid] = useState<string | null>(null);
  const [playlistAnchorEl, setPlaylistAnchorEl] = useState<null | HTMLElement>(null);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [targetPlaylistPid, setTargetPlaylistPid] = useState<string | null>(null);
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [editingVid, setEditingVid] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [editedTitles, setEditedTitles] = useState<Record<string, string>>({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [viewAsVideo, setViewAsVideo] = useState(() => {
    const saved = localStorage.getItem('viewAsVideo');
    return saved ? JSON.parse(saved) : true;
  });
  const [randomMode, setRandomMode] = useState(() => {
    const saved = localStorage.getItem('gridRandomMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [seed, setSeed] = useState<string>(() => Math.random().toString(36).slice(2, 10));
  const firstModeRender = useRef(true);
  const [notification, setNotification] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);
  const [progressMap, setProgressMap] = useState<Record<string, number>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingPreviewVid, setPlayingPreviewVid] = useState<string | null>(null);
  // Hold-to-preview state
  const [hoverPlayingVid, setHoverPlayingVid] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverAudioRef = useRef<HTMLAudioElement | null>(null);
  const hoverVideoRef = useRef<HTMLVideoElement | null>(null);
  const [hoverTime, setHoverTime] = useState(0);
  const [hoverDuration, setHoverDuration] = useState(0);
  const [hoverSliderDragging, setHoverSliderDragging] = useState(false);
  const [hoverSliderHover, setHoverSliderHover] = useState(false);
  const hoverSliderDraggingTimeRef = useRef(0);
  const [playlistDialogOpen, setPlaylistDialogOpen] = useState(false);
  const [deletePlaylistConfirm, setDeletePlaylistConfirm] = useState<{ open: boolean; pid: string | null }>({
    open: false,
    pid: null
  });
  const [deleteVideosConfirm, setDeleteVideosConfirm] = useState<{ open: boolean; vids: string[] }>({
    open: false,
    vids: []
  });
  const [deleteSingleConfirm, setDeleteSingleConfirm] = useState<{ open: boolean; vid: string | null }>({
    open: false,
    vid: null,
  });
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ... [Keep existing logic handlers identical] ...
  const handleTitleUpdate = async (vid: string) => {
    if (!editTitle.trim()) {
      setEditingVid(null);
      return;
    }
    try {
      await axiosInstance.put(`${backendUrl}/api/videos/${vid}/title`, { title: editTitle });
      setVideos((prevVideos) => prevVideos.map((v) => (v.vid === vid ? { ...v, title: editTitle } : v)));
      setEditedTitles((prev) => ({ ...prev, [vid]: editTitle }));
      setNotification({ message: 'Title updated successfully', severity: 'success' });
    } catch (e: any) {
      console.error("Failed to update title:", e);
      setNotification({ message: 'Failed to update title', severity: 'error' });
    } finally {
      setEditingVid(null);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, vid: string) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedVid(vid);
  };

  const fetchPlaylists = useCallback(async () => {
    try {
      const resp = await axiosInstance.get(`${backendUrl}/api/playlists`);
      setPlaylists(resp.data || []);
    } catch (e) {
      console.error('Failed to fetch playlists', e);
    }
  }, [backendUrl]);
  useEffect(() => { fetchPlaylists(); }, [fetchPlaylists]);

  const handlePlaylistClick = (e: React.MouseEvent<HTMLElement>) => {
    setPlaylistAnchorEl(e.currentTarget);
  };

  const handlePlaylistClose = () => {
    setPlaylistAnchorEl(null);
    if (!selectionMode) {
      setTargetPlaylistPid(null);
    }
  };

  const createPlaylist = async () => {
    setPlaylistDialogOpen(true);
  };

  const handlePlaylistCreate = async (playlist: { name: string; description?: string }) => {
    await axiosInstance.post(`${backendUrl}/api/playlists`, playlist);
    fetchPlaylists();
  };

  const deletePlaylist = async (pid: string) => {
    setDeletePlaylistConfirm({ open: true, pid });
  };

  const handleConfirmDeletePlaylist = async () => {
    if (!deletePlaylistConfirm.pid) return;
    
    try {
      await axiosInstance.delete(`${backendUrl}/api/playlists/${deletePlaylistConfirm.pid}`);
      if (targetPlaylistPid === deletePlaylistConfirm.pid) setTargetPlaylistPid(null);
      fetchPlaylists();
    } catch (err) {
      console.error('Failed to delete playlist', err);
    } finally {
      setDeletePlaylistConfirm({ open: false, pid: null });
    }
  };

  const handleCancelDeletePlaylist = () => {
    setDeletePlaylistConfirm({ open: false, pid: null });
  };

  const toggleTargetPlaylist = (pid: string) => {
    if (targetPlaylistPid === pid) {
      setTargetPlaylistPid(null);
      setSelectionMode(false);
    } else {
      setTargetPlaylistPid(pid);
      setSelectionMode(true);
      setSelectedVideos([]);
    }
  };

  const addSelectedToPlaylist = useCallback(async () => {
    if (!targetPlaylistPid || selectedVideos.length === 0) return;
    try {
      await axiosInstance.post(`${backendUrl}/api/playlists/${targetPlaylistPid}/videos`, { vid: selectedVideos });
      setNotification({ message: `Added ${selectedVideos.length} videos to playlist`, severity: 'success' });
      setSelectedVideos([]);
      setSelectionMode(false);
      setTargetPlaylistPid(null);
      handlePlaylistClose();
      fetchPlaylists();
    } catch (err) {
      console.error('Failed to add videos to playlist', err);
      setNotification({ message: 'Failed to add videos to playlist', severity: 'error' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl, targetPlaylistPid, selectedVideos, fetchPlaylists, selectionMode]);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVid(null);
  };

  const handleOptionSelect = (route: string) => {
    if (selectedVid) {
      navigate(`/${route}/${selectedVid}`, { replace: true });
    }
    handleMenuClose();
  };

  const handleDeleteVideo = () => {
    if (!selectedVid) return;
    setDeleteSingleConfirm({ open: true, vid: selectedVid });
    handleMenuClose();
  };

  const handleConfirmDeleteSingle = async () => {
    if (!deleteSingleConfirm.vid) return;
    try {
      await axiosInstance.delete(`${backendUrl}/api/videos`, {
        data: { videos: [deleteSingleConfirm.vid] },
      });
      setNotification({ message: 'Video deleted successfully', severity: 'success' });
      fetchVideos(true);
    } catch (error) {
      console.error('Failed to delete video:', error);
      setNotification({ message: 'Failed to delete video', severity: 'error' });
    } finally {
      setDeleteSingleConfirm({ open: false, vid: null });
    }
  };

  const handleCancelDeleteSingle = () => {
    setDeleteSingleConfirm({ open: false, vid: null });
  };

  const fetchVideos = useCallback(
    async (reset: boolean = false) => {
      if (isLoading || (!hasMore && !reset)) return;
      setIsLoading(true);
      try {
        const response = randomMode
          ? await axiosInstance.get(
              `${backendUrl}/api/videos/random-seeded?seed=${seed}&page=${reset ? 1 : page}&limit=12`
            )
          : await axiosInstance.get(
              `${backendUrl}/api/videos?page=${reset ? 1 : page}&limit=12`
            );
        const newVideos = response.data.videos;
        setVideos((prevVideos) => (reset ? newVideos : [...prevVideos, ...newVideos]));
        setPage((prevPage) => (reset ? 2 : prevPage + 1));
        setHasMore(newVideos.length > 0 && (reset ? 1 : page) < response.data.totalPages);
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [page, isLoading, hasMore, backendUrl, randomMode, seed]
  );

  const toggleRandomMode = () => {
    const next = !randomMode;
    setRandomMode(next);
    setSeed(Math.random().toString(36).slice(2, 10));
    localStorage.setItem('gridRandomMode', JSON.stringify(next));
  };

  // Reset and refetch from page 1 when random mode (or its seed) changes
  useEffect(() => {
    if (firstModeRender.current) {
      firstModeRender.current = false;
      return;
    }
    setVideos([]);
    setPage(1);
    setHasMore(true);
    fetchVideos(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [randomMode, seed]);

  const toggleSelection = (vid: string) => {
    setSelectedVideos((prev) => (prev.includes(vid) ? prev.filter((id) => id !== vid) : [...prev, vid]));
  };

  const handleBulkDelete = () => {
    if (selectedVideos.length === 0) return;
    setDeleteVideosConfirm({ open: true, vids: selectedVideos });
  };

  const handleConfirmDeleteVideos = async () => {
    const vidsToDelete = deleteVideosConfirm.vids;
    if (vidsToDelete.length === 0) {
      setDeleteVideosConfirm({ open: false, vids: [] });
      return;
    }
    try {
      const response = await axiosInstance.delete(`${backendUrl}/api/videos`, {
        data: { videos: vidsToDelete },
      });
      setNotification({
        message: `Successfully deleted ${response.data.successful.length} videos${
          response.data.failedCount > 0 ? ` (${response.data.failedCount} failed)` : ""
        }`,
        severity: "success",
      });
      setSelectedVideos([]);
      setSelectionMode(false);
      fetchVideos(true);
    } catch (error) {
      console.error("Failed to delete videos:", error);
      setNotification({
        message: "Failed to delete videos. Please try again.",
        severity: "error",
      });
    } finally {
      setDeleteVideosConfirm({ open: false, vids: [] });
    }
  };

  const handleCancelDeleteVideos = () => {
    setDeleteVideosConfirm({ open: false, vids: [] });
  };

  const getVideoTitle = (vid: string): string => {
    const list = searchResults && searchResults.length > 0 ? searchResults : videos;
    const video = list.find(v => v.vid === vid);
    return video ? (editedTitles[vid] || video.title) : vid;
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeletingHls, setIsDeletingHls] = useState(false);
  const [isRandomLoading, setIsRandomLoading] = useState(false);

  const handleRandomPlay = async () => {
    setIsRandomLoading(true);
    try {
      const response = await axiosInstance.get(
        `${backendUrl}/api/videos/random?limit=1`
      );
      const randomVideos = response.data.videos || [];
      if (randomVideos.length > 0) {
        stopAllPreviews();
        navigate(`/music/${randomVideos[0].vid}`, { replace: true });
      } else {
        setNotification({ message: 'No videos to play', severity: 'error' });
      }
    } catch (error) {
      console.error('Failed to fetch random video:', error);
      setNotification({ message: 'Failed to fetch random video', severity: 'error' });
    } finally {
      setIsRandomLoading(false);
    }
  };

  const handleBulkDeleteHls = async () => {
    const hlsVideos = selectedVideos.filter((vid) => {
      const list = searchResults && searchResults.length > 0 ? searchResults : videos;
      const v = list.find((v) => v.vid === vid);
      return v?.hasHls;
    });
    if (hlsVideos.length === 0) {
      setNotification({ message: 'No selected videos have HLS files', severity: 'error' });
      return;
    }
    setIsDeletingHls(true);
    try {
      const response = await axiosInstance.delete(`${backendUrl}/api/videos/hls`, {
        data: { videos: hlsVideos },
      });
      setNotification({
        message: `Deleted HLS for ${response.data.successful?.length ?? hlsVideos.length} videos${
          response.data.failedCount > 0 ? ` (${response.data.failedCount} failed)` : ''
        }`,
        severity: 'success',
      });
      setSelectedVideos([]);
      setSelectionMode(false);
      fetchVideos(true);
    } catch (error: any) {
      console.error('Failed to delete HLS:', error);
      setNotification({
        message: error.response?.data?.message || 'Failed to delete HLS files',
        severity: 'error',
      });
    } finally {
      setIsDeletingHls(false);
    }
  };

  const triggerDownloadViaIframe = (url: string) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch (_) {}
    }, 60000);
  };

  const handlePreviewClick = async (e: React.MouseEvent, video: Video) => {
    e.preventDefault();
    e.stopPropagation();

    if (playingPreviewVid === video.vid) {
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingPreviewVid(null);
      }
      return;
    }

    // Stop any existing preview and reset source
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    
    // Pause global audio player if it exists (MusicPlayer)
    document.querySelector('audio')?.pause();

    try {
      // Use the persisted ref instead of creating a new Audio object
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      
      const audio = audioRef.current;
      audio.src = `${backendUrl}/api/preview/${video.vid}`;
      audio.preload = "auto";
      
      setPlayingPreviewVid(video.vid);

      audio.onended = () => {
        setPlayingPreviewVid(null);
      };

      audio.onerror = () => {
        console.error(`Error playing preview for ${video.vid}`);
        setPlayingPreviewVid(null);
      };

      // Handle the play promise to avoid AbortError
      await audio.play();
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // Ignore AbortError - it's caused by rapid play/pause cycles
        return;
      }
      console.error("Preview playback failed:", err);
      setPlayingPreviewVid(null);
    }
  };

  const stopAllPreviews = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setPlayingPreviewVid(null);
  }, []);

  useEffect(() => {
    return () => stopAllPreviews();
  }, [stopAllPreviews]);

  // Stop hover preview
  const stopHoverPreview = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (hoverAudioRef.current) {
      hoverAudioRef.current.pause();
      hoverAudioRef.current.src = "";
    }
    if (hoverVideoRef.current) {
      hoverVideoRef.current.pause();
    }
    setHoverPlayingVid(null);
    setHoverTime(0);
    setHoverDuration(0);
    setHoverSliderDragging(false);
    setHoverSliderHover(false);
  }, []);

  // Start hover preview after 1s delay
  const startHoverPreview = useCallback((video: Video) => {
    if (hoverPlayingVid === video.vid) return;
    stopHoverPreview();

    hoverTimerRef.current = setTimeout(() => {
      setHoverPlayingVid(video.vid);

      if (!viewAsVideo) {
        // Audio preview (video preview is handled by inline <video> in JSX)
        if (!hoverAudioRef.current) {
          hoverAudioRef.current = new Audio();
        }
        const aud = hoverAudioRef.current;
        aud.src = `${backendUrl}/api/preview/${video.vid}`;
        aud.preload = 'auto';
        aud.play().catch(() => {});
        aud.onended = () => {
          setHoverPlayingVid(null);
        };
        aud.onerror = () => {
          setHoverPlayingVid(null);
        };
      }
    }, 1000);
  }, [viewAsVideo, backendUrl, hoverPlayingVid, stopHoverPreview]);

  // Card hover handlers
  const handleCardMouseEnter = useCallback((video: Video) => {
    startHoverPreview(video);
  }, [startHoverPreview]);

  const handleCardMouseLeave = useCallback(() => {
    stopHoverPreview();
  }, [stopHoverPreview]);

  useEffect(() => {
    return () => {
      stopAllPreviews();
      stopHoverPreview();
    };
  }, [stopAllPreviews, stopHoverPreview]);

  const handleBulkDownload = async () => {
    if (selectedVideos.length === 0) return;
    setIsDownloading(true);
    try {
      const CHUNK_SIZE = 10;
      const chunks = [];
      for (let i = 0; i < selectedVideos.length; i += CHUNK_SIZE) {
        chunks.push(selectedVideos.slice(i, i + CHUNK_SIZE));
      }
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        if (chunks.length > 1) {
          setNotification({
            message: `Preparing batch ${chunkIndex + 1} of ${chunks.length}...`,
            severity: "success",
          });
        }
        const prepareResponse = await axiosInstance.post(
          `${backendUrl}/api/videos/download/prepare`,
          { videos: chunk },
        );
        const { token } = prepareResponse.data;
        const downloadUrl = `${backendUrl}/api/videos/download/${token}`;
        triggerDownloadViaIframe(downloadUrl);
        if (chunks.length > 1 && chunkIndex < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
      setNotification({
        message: `Download started for ${selectedVideos.length} video${selectedVideos.length > 1 ? 's' : ''}`,
        severity: "success",
      });
      setSelectedVideos([]);
      setSelectionMode(false);
    } catch (error: any) {
      console.error("Failed to download videos:", error);
      setNotification({
        message: error.response?.data?.message || "Failed to download videos. Please try again.",
        severity: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedVideos([]);
    }
  };

  const handleViewModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setViewAsVideo(newValue);
    localStorage.setItem('viewAsVideo', JSON.stringify(newValue));
  };

  useEffect(() => {
    fetchVideos(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  useEffect(() => {
    let mounted = true;
    const fetchProgressForVisible = async () => {
      const list = (searchResults && searchResults.length > 0 ? searchResults : videos);
      const toPoll = list.filter(v => v.filesize == null).map(v => v.vid);
      if (toPoll.length === 0) return;
      try {
        const promises = toPoll.map(async (vid) => {
          try {
            const resp = await axiosInstance.get(`${backendUrl}/api/progress/${vid}`);
            return { vid, progress: resp.data.progress || 0 };
          } catch (e) {
            return { vid, progress: 0 };
          }
        });
        const results = await Promise.all(promises);
        if (!mounted) return;
        setProgressMap((prev) => {
          const copy = { ...prev };
          results.forEach(r => { if (typeof r.progress === 'number') copy[r.vid] = r.progress; });
          return copy;
        });
        const completed = results.filter(r => typeof r.progress === 'number' && r.progress >= 100).map(r => r.vid);
        if (completed.length > 0) {
          try {
            const statsResults = await Promise.all(completed.map(async (id) => {
              try {
                const resp = await axiosInstance.get(`${backendUrl}/api/stats/${id}`);
                return { id, stats: resp.data };
              } catch (e) {
                return { id, stats: null };
              }
            }));
            const statsMap: Record<string, any> = {};
            statsResults.forEach(r => { if (r.stats) statsMap[r.id] = r.stats; });
            setVideos((prev) => {
              if (Object.keys(statsMap).length === 0) return prev;
              return prev.map(v => {
                const s = statsMap[v.vid];
                if (!s) return v;
                return {
                  ...v,
                  title: s.title ?? v.title,
                  filesize: s.filesize ?? v.filesize,
                  duration: s.duration ?? v.duration,
                  resolution: s.resolution ?? v.resolution,
                } as typeof v;
              });
            });
            setProgressMap((prev) => {
              const copy = { ...prev };
              completed.forEach(id => { copy[id] = 100; });
              return copy;
            });
          } catch (e) { }
        }
      } catch (err) { }
    };
    fetchProgressForVisible();
    const id = setInterval(fetchProgressForVisible, 2000);
    return () => { mounted = false; clearInterval(id); };
  }, [videos, searchResults, backendUrl]);

  const lastVideoElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (searchResults && searchResults.length > 0) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchVideos();
        }
      });
      if (node) observer.current.observe(node);
    },
    [fetchVideos, hasMore, searchResults]
  );

  const formatSeconds = (seconds: number | undefined): string => {
    if (seconds == null || isNaN(seconds)) return 'N/A';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const pad = (n: number) => String(n).padStart(2, '0');
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  return (
    <>
      {selectionMode && (
        <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, bgcolor: 'background.paper', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Toolbar>
            <Typography sx={{ flex: 1, fontWeight: 'bold' }}>
              {selectedVideos.length} selected
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {targetPlaylistPid ? (
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={addSelectedToPlaylist}
                  disabled={selectedVideos.length === 0}
                >
                  Playlist
                </Button>
              ) : (
                <>
                  {isMobile ? (
                    <Tooltip title="Download">
                      <span>
                        <IconButton onClick={handleBulkDownload} disabled={selectedVideos.length === 0 || isDownloading} color="success">
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      onClick={handleBulkDownload}
                      disabled={selectedVideos.length === 0 || isDownloading}
                      color="success"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      size="small"
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Download
                    </Button>
                  )}

                  {isMobile ? (
                    <Tooltip title="Delete HLS">
                      <span>
                        <IconButton onClick={handleBulkDeleteHls} disabled={selectedVideos.length === 0 || isDeletingHls} color="warning">
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      onClick={handleBulkDeleteHls}
                      disabled={selectedVideos.length === 0 || isDeletingHls}
                      color="warning"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      size="small"
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Delete HLS
                    </Button>
                  )}

                  {isMobile ? (
                    <Tooltip title="Delete">
                      <span>
                        <IconButton onClick={handleBulkDelete} disabled={selectedVideos.length === 0} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      onClick={handleBulkDelete}
                      disabled={selectedVideos.length === 0}
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      size="small"
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  )}
                </>
              )}
              <Button color="inherit" onClick={toggleSelectionMode}>Cancel</Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth={false} sx={{ mt: 2, mb: 8 }}>
        {/* Toolbar */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={selectionMode ? "contained" : "outlined"}
              color={selectionMode ? "primary" : "inherit"}
              startIcon={selectionMode ? <CheckIcon /> : <Checkbox checked={selectionMode} size="small" sx={{ p:0 }} />}
              onClick={toggleSelectionMode}
              size="small"
              sx={{ borderRadius: 4, textTransform: 'none' }}
            >
              {selectionMode ? "Done" : "Select"}
            </Button>
            <Button
              variant={randomMode ? "contained" : "outlined"}
              color={randomMode ? "secondary" : "inherit"}
              startIcon={<ShuffleIcon />}
              onClick={toggleRandomMode}
              size="small"
              sx={{ borderRadius: 4, textTransform: 'none' }}
            >
              Random
            </Button>
            <Tooltip title="Playlists">
              <IconButton onClick={handlePlaylistClick} size="small" sx={{ bgcolor: alpha('#fff', 0.05) }}>
                <PlaylistPlayIcon />
              </IconButton>
            </Tooltip>
            {!viewAsVideo && (
              <Tooltip title="Random Play">
                <span>
                  <IconButton
                    onClick={handleRandomPlay}
                    disabled={isRandomLoading}
                    size="small"
                    color="secondary"
                    sx={{ bgcolor: alpha('#fff', 0.05) }}
                  >
                    {isRandomLoading ? <CircularProgress size={18} color="inherit" /> : <ShuffleIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ViewModeSwitch checked={viewAsVideo} onChange={handleViewModeChange} />
          </Box>
        </Box>

        {/* Playlist Menu */}
        <Menu 
          anchorEl={playlistAnchorEl} 
          open={Boolean(playlistAnchorEl)} 
          onClose={handlePlaylistClose}
          PaperProps={{
            sx: { 
              bgcolor: 'background.paper', 
              borderRadius: 3,
              mt: 1,
              boxShadow: '0 12px 35px -5px rgba(0, 0, 0, 0.65)',
              border: '1px solid rgba(255,255,255,0.07)',
              minWidth: 320,
            }
          }}
        >
          {/* Create playlist */}
          <MenuItem dense
            onClick={createPlaylist} 
            sx={{ 
              py: 1.5,
              px: 3,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
            }}
          >
            <AddIcon sx={{ mr: 1.5, color: 'primary.main' }} /> 
            <Typography fontWeight={500}>Create new playlist</Typography>
          </MenuItem>

          <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

          {playlists.map((p) => (
            <MenuItem dense
              key={p.pid} 
              sx={{ 
                px: 2.5,
                py: 1.1,
                '&:hover': { bgcolor: 'action.hover' },
                ...(targetPlaylistPid === p.pid && {
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                })
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1.5 }}>
                {/* Checkbox still only triggers selection */}
                <Checkbox 
                  checked={targetPlaylistPid === p.pid} 
                  size="small" 
                  onChange={() => toggleTargetPlaylist(p.pid)}
                />
                
                {/* Playlist name – clickable → navigate */}
                <Typography 
                  sx={{ 
                    cursor: 'pointer', 
                    flex: 1,
                    fontWeight: targetPlaylistPid === p.pid ? 500 : 400,
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => { window.location.href = `/playlists/${p.pid}`; }}
                >
                  {p.name}
                </Typography>
              </Box>

              {/* Delete icon – only visible when this playlist is selected */}
              {targetPlaylistPid === p.pid && (
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    deletePlaylist(p.pid);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </MenuItem>
          ))}

          {/* Add selected action – more prominent & styled */}
          {selectionMode && targetPlaylistPid && (
            <MenuItem 
              onClick={addSelectedToPlaylist} 
              sx={{ 
                mx: 2,
                mt: 1,
                borderRadius: 2,
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                fontWeight: 500,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.22),
                }
              }}
            >
              <AddIcon sx={{ mr: 1.5 }} /> 
              Add selected videos to playlist
            </MenuItem>
          )}
        </Menu>

        {/* Grid */}
        <Box display="grid" sx={{ 
          gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, 
          gap: 3 
        }}>
          {(searchResults && searchResults.length > 0 ? searchResults : videos).map((video, index) => (
            <Card
              key={video.vid}
              ref={index === (searchResults && searchResults.length > 0 ? searchResults : videos).length - 1 ? lastVideoElementRef : null}
              onMouseEnter={() => handleCardMouseEnter(video)}
              onMouseLeave={handleCardMouseLeave}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: selectionMode && selectedVideos.includes(video.vid) ? '2px solid' : '1px solid',
                borderColor: selectionMode && selectedVideos.includes(video.vid) ? 'primary.main' : 'rgba(255,255,255,0.1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  borderColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <Box
                component={selectionMode || (viewAsVideo && hoverPlayingVid === video.vid) ? "div" : Link}
                to={selectionMode || (viewAsVideo && hoverPlayingVid === video.vid) ? undefined : `/${viewAsVideo ? 'video' : 'music'}/${video.vid}`}
                onClick={(e: React.MouseEvent) => {
                  if (selectionMode) {
                    toggleSelection(video.vid);
                  } else {
                    if (!viewAsVideo && video.duration && video.duration > 600) {
                      if (!window.confirm("You are about to play a long video (>10 minutes) in Audio mode. Are you sure?")) {
                        e.preventDefault();
                      }
                    }
                  }
                }}
                sx={{
                  position: "relative",
                  paddingTop: "56.25%",
                  cursor: selectionMode ? "default" : "pointer",
                  bgcolor: 'black',
                  '&:hover .mode-overlay': { opacity: selectionMode ? 0 : 1 }
                }}
              >
                {!selectionMode && (
                  <Box
                    className="mode-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      zIndex: 2,
                      color: 'white',
                      pointerEvents: 'none'
                    }}
                  >
                    {viewAsVideo ? (
                      <>
                        <PlayArrowIcon sx={{ fontSize: 64, color: 'white', filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Play Video</Typography>
                      </>
                    ) : (
                      <>
                        <MusicNoteIcon sx={{ fontSize: 64, color: 'white', filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Play Audio</Typography>
                      </>
                    )}
                  </Box>
                )}
                {selectionMode && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 2,
                      pointerEvents: 'auto'
                    }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelection(video.vid); }}
                  >
                    <Checkbox
                      checked={selectedVideos.includes(video.vid)}
                      onChange={() => toggleSelection(video.vid)}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        borderRadius: 1,
                        opacity: 1,
                        visibility: 'visible',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        '&.Mui-checked': { color: 'primary.main' }
                      }}
                    />
                  </Box>
                )}
                <Box
                  component="img"
                  src={`${backendUrl}/api/thumbnail/${video.vid}`}
                  alt={video.title}
                  onError={(e: any) => { e.currentTarget.onerror = null; e.currentTarget.src = '/404notfound.jpg'; }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: hoverPlayingVid === video.vid ? 0 : 0.9,
                    transition: 'opacity 0.3s',
                    pointerEvents: hoverPlayingVid === video.vid ? 'none' : 'auto',
                    zIndex: 1
                  }}
                />

                {/* Hover video preview — replaces thumbnail after 1s hold */}
                {hoverPlayingVid === video.vid && viewAsVideo && (
                  <>
                    <video
                      key={video.vid}
                      autoPlay
                      playsInline
                      ref={(node) => {
                        if (node) {
                          hoverVideoRef.current = node;
                          if (!node.src) {
                            node.src = `${backendUrl}/api/v/${video.vid}`;
                          }
                        }
                      }}
                      onTimeUpdate={() => {
                        if (!hoverSliderDragging && hoverVideoRef.current) {
                          setHoverTime(hoverVideoRef.current.currentTime);
                        }
                      }}
                      onLoadedMetadata={() => {
                        if (hoverVideoRef.current) {
                          setHoverDuration(hoverVideoRef.current.duration);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        zIndex: 5,
                        backgroundColor: 'black'
                      }}
                    />
                    {/* Time slider */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 6,
                        px: 0.5,
                        pb: 0.5,
                        pt: 1,
                        opacity: hoverSliderHover || hoverSliderDragging ? 1 : 0,
                        transition: 'opacity 0.25s',
                        pointerEvents: hoverSliderHover || hoverSliderDragging ? 'auto' : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: 3,
                          bgcolor: 'rgba(255,255,255,0.3)',
                          borderRadius: 1,
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'height 0.1s',
                          '&:hover': {
                            height: 5,
                          },
                        }}
                        onMouseEnter={() => setHoverSliderHover(true)}
                        onMouseLeave={() => {
                          setHoverSliderHover(false);
                          if (hoverSliderDragging) {
                            setHoverSliderDragging(false);
                          }
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setHoverSliderDragging(true);
                          const rect = e.currentTarget.getBoundingClientRect();
                          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                          const seekTime = pct * (hoverDuration || video.duration || 0);
                          hoverSliderDraggingTimeRef.current = seekTime;
                          if (hoverVideoRef.current) {
                            hoverVideoRef.current.currentTime = seekTime;
                          }
                          setHoverTime(seekTime);
                        }}
                        onMouseMove={(e) => {
                          if (!hoverSliderDragging) return;
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                          const seekTime = pct * (hoverDuration || video.duration || 0);
                          hoverSliderDraggingTimeRef.current = seekTime;
                          if (hoverVideoRef.current) {
                            hoverVideoRef.current.currentTime = seekTime;
                          }
                          setHoverTime(seekTime);
                        }}
                        onMouseUp={(e) => {
                          e.stopPropagation();
                          setHoverSliderDragging(false);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        {/* Fill bar */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${hoverDuration ? (hoverSliderDragging ? hoverSliderDraggingTimeRef.current : hoverTime) / hoverDuration * 100 : 0}%`,
                            bgcolor: 'primary.main',
                            borderRadius: 1,
                          }}
                        />
                        {/* Thumb */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: `${hoverDuration ? (hoverSliderDragging ? hoverSliderDraggingTimeRef.current : hoverTime) / hoverDuration * 100 : 0}%`,
                            width: 10,
                            height: 10,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            opacity: hoverSliderDragging || hoverSliderHover ? 1 : 0,
                            transition: 'opacity 0.15s',
                            pointerEvents: 'none',
                          }}
                        />
                      </Box>
                    </Box>
                    {/* Bottom hover zone — triggers slider visibility */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '25%',
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        pb: 0.5,
                      }}
                      onMouseEnter={() => setHoverSliderHover(true)}
                      onMouseLeave={() => setHoverSliderHover(false)}
                    />
                  </>
                )}

                {/* Hover audio playing indicator */}
                {hoverPlayingVid === video.vid && !viewAsVideo && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-end',
                      height: 40
                    }}>
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 4,
                            bgcolor: 'primary.main',
                            borderRadius: 1,
                            animation: `hoverBar 0.8s ease-in-out ${i * 0.1}s infinite alternate`,
                            '@keyframes hoverBar': {
                              '0%': { height: 8 },
                              '100%': { height: 36 }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {/* Overlays */}
                 <Box sx={{
                   position: 'absolute',
                   bottom: 0,
                   left: 0,
                   right: 0,
                   background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                   p: 2,
                   pt: 4,
                   display: 'flex',
                   justifyContent: 'space-between',
                   alignItems: 'flex-end',
                   zIndex: 4
                 }}>
                   {video.hasHls && (
                     <Box sx={{ 
                       bgcolor: 'rgba(255,255,255,0.2)', 
                       backdropFilter: 'blur(4px)',
                       px: 0.75, 
                       py: 0.25, 
                       borderRadius: 0.5, 
                       fontSize: 10, 
                       fontWeight: 'bold',
                       color: 'white',
                       border: '1px solid rgba(255,255,255,0.1)'
                     }}>HLS</Box>
                   )}
                   <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
                      {/* Preview Button - Elegant Pill Design */}
                      {!viewAsVideo && video.previewFileName && (
                        <Tooltip title="Preview Song">
                          <Box 
                            onClick={(e) => handlePreviewClick(e, video)}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              cursor: 'pointer',
                              px: 1, 
                              py: 0.3, 
                              borderRadius: 1, 
                              bgcolor: playingPreviewVid === video.vid ? 'primary.main' : alpha(theme.palette.common.white, 0.1),
                              color: 'white',
                              transition: 'all 0.2s ease',
                              border: '1px solid rgba(255,255,255,0.1)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': { 
                                bgcolor: playingPreviewVid === video.vid ? 'primary.dark' : alpha(theme.palette.common.white, 0.2),
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                              },
                              ...(playingPreviewVid === video.vid && {
                                boxShadow: '0 0 12px rgba(25, 118, 210, 0.6)',
                                '&:hover': { transform: 'scale(1.05)' }
                              })
                            }}
                          >
                            <PlayCircleFilledIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption" sx={{ 
                              fontWeight: 'bold', 
                              fontSize: 10, 
                              lineHeight: 1, 
                              textTransform: 'uppercase', 
                              letterSpacing: 0.5 
                            }}>
                              {playingPreviewVid === video.vid ? 'Playing' : 'Preview'}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                      <Box sx={{ 
                        bgcolor: viewAsVideo ? 'primary.main' : 'secondary.main', 
                        px: 0.5, 
                        py: 0.25, 
                        borderRadius: 0.5, 
                        display: 'flex',
                        alignItems: 'center',
                        color: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.5)'
                      }}>
                        {viewAsVideo ? <PlayArrowIcon sx={{ fontSize: 14 }} /> : <MusicNoteIcon sx={{ fontSize: 14 }} />}
                      </Box>
                      {video.duration !== undefined && (
                        <Box sx={{ 
                          bgcolor: 'rgba(0,0,0,0.7)', 
                          px: 0.75, 
                          py: 0.25, 
                          borderRadius: 0.5, 
                          fontSize: 11, 
                          fontFamily: 'monospace',
                          color: 'white'
                        }}>
                          {formatSeconds(video.duration)}
                        </Box>
                      )}
                    </Box>
                 </Box>

                {progressMap[video.vid] > 0 && progressMap[video.vid] < 100 && (
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                    <LinearProgress variant="determinate" value={progressMap[video.vid]} sx={{ height: 4 }} />
                  </Box>
                )}
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 2, pb: '16px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  {editingVid === video.vid ? (
                     <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                     <TextField
                       value={editTitle}
                       onChange={(e) => setEditTitle(e.target.value)}
                       variant="standard"
                       size="small"
                       autoFocus
                       onBlur={() => { setEditingVid(null); setEditTitle(''); }}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter') handleTitleUpdate(video.vid);
                         if (e.key === 'Escape') setEditingVid(null);
                       }}
                       fullWidth
                     />
                     <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleTitleUpdate(video.vid)}>
                       <CheckIcon fontSize="small" color="primary" />
                     </IconButton>
                   </Box>
                  ) : (
                    <Typography 
                      variant="subtitle2" 
                      onClick={() => { setEditingVid(video.vid); setEditTitle(editedTitles[video.vid] || video.title); }}
                      sx={{ 
                        fontWeight: 600, 
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {editedTitles[video.vid] || video.title}
                    </Typography>
                  )}
                  
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleMenuClick(e, video.vid)}
                    sx={{ mt: -0.5, mr: -1, color: 'text.secondary' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     {video.filesize ? `${(video.filesize / 1048576).toFixed(1)} MB` : 'Processing'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {video.resolution || 'N/A'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => handleOptionSelect("music")}><MusicNoteIcon sx={{ mr: 1.5, fontSize: 20 }} /> Play as Music</MenuItem>
          <MenuItem onClick={() => handleOptionSelect("video")}><PlayArrowIcon sx={{ mr: 1.5, fontSize: 20 }} /> Play as Video</MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleDeleteVideo} sx={{ color: 'error.main' }}><DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} /> Delete</MenuItem>
        </Menu>

        {isLoading && (
          <Box sx={{ width: '100%', mt: 4 }}>
            <LinearProgress color="secondary" sx={{ borderRadius: 1 }} />
          </Box>
        )}
        
        {!hasMore && !searchResults && (
          <Typography align="center" sx={{ mt: 4, color: 'text.secondary', fontStyle: 'italic' }}>
            You've reached the end
          </Typography>
        )}

        <Snackbar
          open={!!notification}
          autoHideDuration={4000}
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setNotification(null)} severity={notification?.severity} variant="filled" sx={{ width: "100%" }}>
            {notification?.message}
          </Alert>
        </Snackbar>

        <PlaylistDialog
          open={playlistDialogOpen}
          onClose={() => setPlaylistDialogOpen(false)}
          onSuccess={handlePlaylistCreate}
        />

        <ConfirmDialog
          open={deletePlaylistConfirm.open}
          title="Delete Playlist"
          message="Are you sure you want to delete this playlist? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirmColor="error"
          onConfirm={handleConfirmDeletePlaylist}
          onCancel={handleCancelDeletePlaylist}
        />

        <ConfirmDialog
          open={deleteSingleConfirm.open}
          title="Delete Video"
          message={`Are you sure you want to delete "${getVideoTitle(deleteSingleConfirm.vid!)}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirmColor="error"
          onConfirm={handleConfirmDeleteSingle}
          onCancel={handleCancelDeleteSingle}
        />

        <ConfirmDialog
          open={deleteVideosConfirm.open}
          title={`Delete ${deleteVideosConfirm.vids.length} Video${deleteVideosConfirm.vids.length > 1 ? 's' : ''}?`}
          message={
            <Box sx={{ maxWidth: 600 }}>
              <Typography variant="body2" paragraph>
                Are you sure you want to delete the selected video{deleteVideosConfirm.vids.length > 1 ? 's' : ''}? This action cannot be undone.
              </Typography>
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="video-list-content"
                  id="video-list-header"
                >
                  <Typography>
                    {deleteVideosConfirm.vids.length} video{deleteVideosConfirm.vids.length > 1 ? 's' : ''} to delete
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List sx={{ p: 0, maxWidth: 500 }}>
                    {deleteVideosConfirm.vids.map((vid, index) => (
                      <ListItem key={vid} divider={index < deleteVideosConfirm.vids.length - 1}>
                        <ListItemText
                          primary={getVideoTitle(vid)}
                          primaryTypographyProps={{
                            noWrap: true,
                            variant: "body2",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          }
          confirmLabel={`Delete ${deleteVideosConfirm.vids.length} Video${deleteVideosConfirm.vids.length > 1 ? 's' : ''}`}
          cancelLabel="Cancel"
          confirmColor="error"
          onConfirm={handleConfirmDeleteVideos}
          onCancel={handleCancelDeleteVideos}
        />
      </Container>
    </>
  );
};

export default VideoGrid;
